import { type ReactNode, useMemo } from "react";
import { Navigate } from "react-router-dom";

import AppAccessDenied from "@/components/sitehub-ui/molecules/AppAccessDenied";
import { PAGE_URL } from "@/data_source/constants";
import AppLoading from "@/pages/AppLoading";
import { useUserAuth } from "@/utils/auth";

import type { RouterValidator } from ".";

type Props = {
  validator?: RouterValidator;
  children: ReactNode;
};

/**
 * Reactive per-route access guard.
 *
 * Unlike building the router with the access decision baked in, this component
 * re-evaluates the validator whenever auth/workspace-context changes, so the
 * browser router is created only once and never rebuilt on a context switch.
 *
 * Evaluation order matters:
 * 1. If the route is allowed (including no-guard routes such as the `/` layout
 *    that loads the workspace context), render it right away. This is what lets
 *    the context load in the first place and avoids a loader deadlock.
 * 2. If it is not allowed but the initial context load hasn't finished yet,
 *    show a loader instead of mounting the page or bouncing the URL. This blocks
 *    invalid routes early (no operational queries) while preserving valid deep
 *    links until the real context is known.
 * 3. If it is not allowed and the context is already resolved, redirect to the
 *    safe route for the active context.
 *
 * Session bootstrap (`CurrentUserBootstrap` in RouterAppGuard) must run above
 * this guard: waiting on UserStore must not block the only `/api/users/me`
 * subscriber, or the loader deadlocks forever.
 */
export function RouteGuard({ validator, children }: Props): JSX.Element {
  const { user, workspace, workspaceContext, isModuleAuthorized } =
    useUserAuth();
  const localToken = useMemo(() => localStorage.getItem("token"), []);

  const isAllowed: "valid" | "invalid" | "not_configured" | "waiting" =
    useMemo(() => {
      switch (true) {
        case !validator || (!user && !!localToken?.length):
          return "waiting";
        default:
          if (
            validator?.validate(
              user,
              isModuleAuthorized,
              workspace,
              workspaceContext
            )
          ) {
            return "valid";
          }
          return validator?.failureReason?.(user, workspaceContext) ===
            "access_not_configured"
            ? "not_configured"
            : "invalid";
      }
    }, [
      isModuleAuthorized,
      localToken,
      user,
      validator,
      workspace,
      workspaceContext
    ]);
  switch (isAllowed) {
    case "valid":
      return <>{children}</>;
    case "not_configured":
      return <Navigate to={PAGE_URL.ACCESS_NOT_CONFIGURED} replace />;
    case "invalid":
      if (validator?.opts.renderAccessDenied) {
        return (
          <AppAccessDenied
            testId="route-access-denied"
            message={validator.opts.accessDeniedMessage}
          />
        );
      }
      return (
        <Navigate to={validator?.opts.fallbackPath ?? PAGE_URL.HOME} replace />
      );
    default:
      return <AppLoading />;
  }
}
